import {
  createContext, useContext, useEffect, useMemo, useState,
} from 'react';
import GetOrganisationsSettings from '../../services/adapters/organisationSettings';

export const OrganisationSettingsContext = createContext<{
  campaignsEnabled: boolean;
  sendToCalculatorEnabled: boolean;
  loading: boolean;
  campaignError: string | undefined;
  calculatorError: string | undefined;
} | undefined>(undefined);

export function useCampaignsEnabled() {
  const context = useContext(OrganisationSettingsContext);

  if (context === undefined) {
    throw new Error('useCampaignsEnabled must be used within the OrganisationSettingsProvider');
  }

  return context;
}

export function useSendToCalculatorEnabled() {
  const context = useContext(OrganisationSettingsContext);

  if (context === undefined) {
    throw new Error('useSendToCalculatorEnabled must be used within the OrganisationSettingsProvider');
  }

  return context;
}

export default function OrganisationSettingsProvider({ children }: { children: React.ReactNode }) {
  const [campaignsEnabled, setCampaignsEnabled] = useState<boolean>(false);
  const [sendToCalculatorEnabled, setSendToCalculatorEnabled] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [campaignError, setCampaignError] = useState<string | undefined>(undefined);
  const [calculatorError, setCalculatorError] = useState<string | undefined>(undefined);

  useEffect(() => {
    const getOrganisationSettings = async () => {
      try {
        const organisationAdapter = new GetOrganisationsSettings();
        const settings = await organisationAdapter.adaptResponse();
        setCampaignsEnabled(settings.isCampaignEnabled);
        setSendToCalculatorEnabled(settings.isSendToCalculatorEnabled);
        if (!settings.isCampaignEnabled) {
          setCampaignError('Organisation does not have campaigns enabled. Please contact your account manager or email helpdesk@policyinpractice.co.uk');
        }
        if (!settings.isSendToCalculatorEnabled) {
          setCalculatorError('Organisation does not have LIFT-2-Calculator enabled. Please contact your account manager or email helpdesk@policyinpractice.co.uk');
        }
      } catch (err) {
        setCampaignError('Failed to load organisation settings');
        setCalculatorError('Failed to load organisation settings');
      } finally {
        setLoading(false);
      }
    };
    getOrganisationSettings();
  }, []);

  const contextValue = useMemo(() => ({
    campaignsEnabled,
    sendToCalculatorEnabled,
    loading,
    campaignError,
    calculatorError,
  }), [campaignsEnabled, sendToCalculatorEnabled, loading, campaignError, calculatorError]);

  return (
    <OrganisationSettingsContext.Provider value={contextValue}>
      {children}
    </OrganisationSettingsContext.Provider>
  );
}
